import React, { useEffect, useState } from 'react';
import { ML_EXEMPTION_CATEGORIES, getMlExemptionSuggestion } from '../../../../utils/mlExemptionCategories';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import InfoHint from '../../SharedComponents/InfoHint';
import SettingsSelect from '../SettingsSelect';

// The momsregistrering fields — momsnummer, momsstatus, undantagsgrund and
// momsprofil (the facts the momsdeklaration deadline rules read).
//
// These used to sit in the middle of the Företagsuppgifter form, which meant
// half of a tenant's VAT configuration lived under "company identity" and the
// other half (avdragsprofil, registreringshistorik) under "Bokföring". Nobody
// looking for momsinställningar would guess either. They now render on the
// Moms page; the state and the save still belong to the CompanySettings shell
// and arrive as props, so this is a presentation split, not a data one.
const VAT_STATUS_OPTIONS = [
  { value: 'registered', label: 'Momsregistrerad (full moms)' },
  { value: 'exempt', label: 'Aktivitetsbefriad (ML 10 kap)' },
  { value: 'not_registered', label: 'Ej momsregistrerad (under tröskel, ML 18 kap)' },
];

const PS_FILING_OPTIONS = [
  { value: '', label: 'Inte angivet' },
  { value: 'electronic', label: 'E-tjänst eller filöverföring' },
  { value: 'paper', label: 'Pappersblankett (SKV 5740)' },
];

const CompanyVatFields = ({ companyDetails, setCompanyDetails, companyId }) => {
  // B14 — the PS quarterly threshold is a value Skatteverket decides, held in
  // vat_regulatory_parameters (R-PS-2). Writing "500 000 kr" into this copy
  // would be a second place for it to be wrong when it changes.
  const [psQuarterlyMax, setPsQuarterlyMax] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(getApiUrl(`${companyId}/reporting/vat-advisories`), { withCredentials: true })
      .then((response) => {
        const parameter = response.data?.parameters?.ps_quarterly_max_sek;
        if (!cancelled && parameter?.value) setPsQuarterlyMax(parameter.value);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [companyId]);

  const psQuarterlyMaxLabel = psQuarterlyMax
    ? `${new Intl.NumberFormat('sv-SE').format(psQuarterlyMax)} kr`
    : 'gränsbeloppet';

  return (
    <div className="form-fields">
      <div className="form-field half-width">
        <label htmlFor="vatNumber" className="modern-label">
          Momsnummer
        </label>
        {(companyDetails.vatStatus === 'exempt' || companyDetails.vatStatus === 'not_registered') ? (
          <input
            type="text"
            className="modern-input"
            id="vatNumber"
            value="n/a — momsbefriad"
            disabled
            title="Momsnummer visas inte på fakturor när tenanten är momsbefriad (ML 17 kap)"
          />
        ) : (
          <input
            type="text"
            className="modern-input"
            id="vatNumber"
            placeholder="SE123456789001"
            value={companyDetails.vatNumber}
            onChange={(e) => setCompanyDetails({ ...companyDetails, vatNumber: e.target.value })}
          />
        )}
      </div>

      {/* Session 3 — Momsstatus per ML 10 / 18 kap */}
      <div className="form-field half-width">
        <label htmlFor="vatStatus" className="modern-label">
          Momsstatus
          <InfoHint text="Påverkar fakturatemplate och bokföring av in-/utgående moms." />
        </label>
        <SettingsSelect
          id="vatStatus"
          options={VAT_STATUS_OPTIONS}
          value={companyDetails.vatStatus || 'registered'}
          onChange={(value) => setCompanyDetails({
            ...companyDetails,
            vatStatus: value,
            // Clear stale reason when leaving exempt mode
            vatExemptionReason: value === 'exempt' ? companyDetails.vatExemptionReason : '',
          })}
          isSearchable={false}
        />
      </div>

      {companyDetails.vatStatus === 'exempt' && (
        <>
          {/* Session 4 — kategori-picker for vat_exemption_reason. Auto-fills the
              free-text textarea below; editing remains supported (Skatteverket
              fakturering: chapter-level citation suffices, ML 17 kap). */}
          <div className="form-field full-width">
            <label htmlFor="vatExemptionCategory" className="modern-label">
              Välj kategori (ML 10 kap)
              <InfoHint text="Picker fyller textarean nedan. Du kan editera fritt efter val." />
            </label>
            {/* Deliberately never holds a value: it fills the textarea below,
                which is where the canonical text lives, and resets so another
                kategori can be picked. */}
            <SettingsSelect
              id="vatExemptionCategory"
              options={ML_EXEMPTION_CATEGORIES.map((c) => ({ value: c.id, label: c.label }))}
              value={null}
              onChange={(value) => {
                const suggestion = getMlExemptionSuggestion(value);
                if (suggestion) {
                  setCompanyDetails({
                    ...companyDetails,
                    vatExemptionReason: suggestion,
                  });
                }
              }}
              placeholder="Välj en vanlig kategori (eller skriv fritt nedan)…"
            />
          </div>

          <div className="form-field full-width">
            <label htmlFor="vatExemptionReason" className="modern-label">
              Grund för undantag (ML 10 kap) *
              <InfoHint text="Texten visas på fakturan (ML 17 kap fakturainnehåll). Krävs vid Aktivitetsbefriad." />
            </label>
            <textarea
              id="vatExemptionReason"
              className="modern-input"
              rows={2}
              placeholder="Ex: Sjukvård enligt mervärdesskattelagen 10 kap"
              value={companyDetails.vatExemptionReason || ''}
              onChange={(e) => setCompanyDetails({ ...companyDetails, vatExemptionReason: e.target.value })}
            />
          </div>
        </>
      )}

      {/* Momsprofil — the facts the momsdeklaration deadline rules
          need. Without them every helårsmoms tenant silently got the
          standard monthly formula and the 26th-track was unreachable
          (G2, G3). Hidden for a momsbefriad tenant, which files no
          momsdeklaration at all. */}
      {companyDetails.vatStatus !== 'exempt'
        && companyDetails.vatStatus !== 'not_registered' && (
        <>
          <div className="form-field full-width">
            <label className="modern-label">Momsprofil</label>
            <small className="text-muted d-block mb-2">
              Styr vilken förfallodag som gäller för momsdeklarationen
              (Skatteverket, <em>När ska jag deklarera moms</em>).
            </small>
          </div>

          <div className="form-field half-width">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="euTrade"
                checked={Boolean(companyDetails.euTrade)}
                onChange={(e) => setCompanyDetails({
                  ...companyDetails, euTrade: e.target.checked,
                })}
              />
              <label className="form-check-label" htmlFor="euTrade">
                Handel med andra EU-länder
              </label>
              <InfoHint text="Vid helårsmoms flyttas förfallodagen tidigare, till den 26:e i andra månaden efter beskattningsåret. Lämna rutan omarkerad bara om företaget verkligen saknar EU-handel — annars visas en förfallodag som är för sen. Styr även skyldigheten att lämna periodisk sammanställning." />
            </div>
          </div>

          <div className="form-field half-width">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="vatLargeCompany"
                checked={Boolean(companyDetails.vatLargeCompany)}
                onChange={(e) => setCompanyDetails({
                  ...companyDetails, vatLargeCompany: e.target.checked,
                })}
              />
              <label className="form-check-label" htmlFor="vatLargeCompany">
                Beskattningsunderlag över 40 miljoner kronor
              </label>
                <InfoHint text="Momsen ska då redovisas per kalendermånad och deklarationen lämnas den 26:e i månaden efter perioden." />
            </div>
          </div>

          {companyDetails.companyType === 'Enskild firma' && (
            <div className="form-field half-width">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="byraanstand"
                  checked={Boolean(companyDetails.byraanstand)}
                  onChange={(e) => setCompanyDetails({
                    ...companyDetails, byraanstand: e.target.checked,
                  })}
                />
                <label className="form-check-label" htmlFor="byraanstand">
                  Byråanstånd
                </label>
                <InfoHint text="Flyttar helårsmomsens förfallodag från 12 maj till 26 juni." />
              </div>
            </div>
          )}

          {/* Periodisk sammanställning (R-PS-2/R-PS-3). Shown only for
              a tenant that trades within the EU — for everyone else
              these two settings have nothing to act on. */}
          {Boolean(companyDetails.euTrade) && (
            <>
              <div className="form-field half-width">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="psQuarterlyApproved"
                    checked={Boolean(companyDetails.psQuarterlyApproved)}
                    onChange={(e) => setCompanyDetails({
                      ...companyDetails, psQuarterlyApproved: e.target.checked,
                    })}
                  />
                  <label className="form-check-label" htmlFor="psQuarterlyApproved">
                    Skatteverket har godkänt kvartalsvis periodisk sammanställning
                  </label>
                  <InfoHint text={`Varuförsäljning till EU rapporteras annars varje månad. `
                    + `Godkännandet förutsätter att försäljningen inte överstigit `
                    + `${psQuarterlyMaxLabel} under ansökningskvartalet eller något av de `
                    + 'fyra föregående kvartalen.'} />
                </div>
              </div>

              <div className="form-field half-width">
                <label className="form-label" htmlFor="psFilingChannel">
                  Inlämningssätt för periodisk sammanställning
                  <InfoHint text="Pappersblanketten ska vara inne den 20:e, e-tjänsten den 25:e i månaden efter perioden. Är inlämningssättet inte angivet visas det tidigare datumet — det är alltid i tid." />
                </label>
                <SettingsSelect
                  id="psFilingChannel"
                  options={PS_FILING_OPTIONS}
                  value={companyDetails.psFilingChannel || ''}
                  onChange={(value) => setCompanyDetails({
                    ...companyDetails, psFilingChannel: value || null,
                  })}
                  isSearchable={false}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CompanyVatFields;
